<template>
  <section class="distributed-hybrid column items-center q-pt-md" v-if="!appStore.isPageLoading">
    <runai-stepper :steps="steps" :current-step="currentStep" :header-navigation="false" @step-changed="onStepClicked">
      <template #step>
        <template v-if="currentStep === EDistributedReplicaType.Worker">
          <distributed-hybrid-worker
            :secrets="secrets"
            :submitting="submitting"
            :distributed-model="distributedModel"
            :project-data="projectData"
            :policy="workerPolicy"
            :policy-view-details="policyViewDetails"
            :environment-variables-source-value-options="environmentVariablesSourceValueOptions"
            :allowed-data-sources="allowedDatasourceOptions"
            @update="distributedModel = $event"
            @continue="handleNextStep"
            @save-form-state="saveWorkerFormState"
            @leave-page="redirectToPrevRoute"
          />
        </template>
        <template v-else-if="currentStep === EDistributedReplicaType.Master">
          <distributed-hybrid-master
            :secrets="secrets"
            :form-meta-data="masterMetaData"
            :master-spec="distributedModel.masterSpec || {}"
            :project-data="projectData"
            :policy="masterPolicy"
            :policy-view-details="policyViewDetails"
            :submitting="submitting"
            :environment-variables-source-value-options="environmentVariablesSourceValueOptions"
            :allowed-data-sources="allowedDatasourceOptions"
            @back="onBack"
            @submit="onSubmit"
            @save-form-state="saveMasterFormState"
            @update-same-as-worker="setSameAsWorker"
            @update="updateMasterSpec"
            @leave-page="redirectToPrevRoute"
          />
        </template>
      </template>
    </runai-stepper>
  </section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { IStepsConfig } from "@/components/common/runai-stepper";
import { RunaiStepper } from "@/components/common/runai-stepper";
import type { IUISuperWorkloadCreationRequest, IUISuperWorkloadSpec } from "@/models/workload.model";
import { EDistributedReplicaType, EWorkloadFormType, EWorkloadType } from "@/models/workload.model";
import { useAppStore } from "@/stores/app.store";
import type { TDistributedMasterFormMetaData } from "./distributed-hybrid-master.vue";
import DistributedHybridMaster from "./distributed-hybrid-master.vue";
import DistributedHybridWorker from "./distributed-hybrid-worker.vue";
import { alertUtil } from "@/utils/alert.util";
import {
  DistributedCleanPodPolicy,
  type UnifiedPolicyInfoPerReplicaType,
  type UnifiedProjectInfo,
  type UnifiedWorkload,
  type UnifiedWorkloadRequest,
} from "@/swagger-models/workloads-client";
import { workloadHybridUtil } from "@/utils/workload-hybrid.util/workload-hybrid.util";
import type { THybridFormState, THybridSelectedTemplates } from "@/models/workload-hybrid.model";
import { useClusterStore } from "@/stores/cluster.store";
import { useTrainingStore } from "@/stores/training.store";
import { useAuthStore } from "@/stores/auth.store";
import type { IPolicyViewDetails } from "@/utils/policy.util/policy-view.util";
import { getDistributedPolicyViewDetails } from "@/utils/policy.util/policy-view.util";
import { DistributedFramework, Scope, type StringOption } from "@/swagger-models/assets-service-client";
import { useEnvironmentVariablesSection } from "@/composables/use-environment-variables-section.composable";
import { deepCopy } from "@/utils/common.util";
import { workloadService } from "@/services/cluster/workload.service/workload.service";
import type { TProjectData } from "./distributed-hybrid-worker.vue";
import { policyUtil } from "@/utils/policy.util";
import { trainingService } from "@/services/control-plane/training.service/training.service";
import { ErrorAlert } from "@/utils/error-alert.util";
import { WORKLOAD_ROUTE_NAMES } from "@/router/workloads.routes/workloads.routes.names";
import type { SecretListInfo } from "@/swagger-models/k8s-objects-tracker-client";
import { credentialService } from "@/services/control-plane/credential.service/credential.service";
import { useDataSourceStore } from "@/stores/data-source.store";
import type { TDataSourceKinds } from "@/models/data-source.model";
import { useWorkloadStore } from "@/stores/workload.store";
import { MIN_CLUSTER_VERSION_CLEAN_POD_POLICY } from "@/common/version.constant";

const allSteps: Array<IStepsConfig> = [
  { name: EDistributedReplicaType.Worker, title: "Workers", icon: "fa-solid fa-check", done: false },
  { name: EDistributedReplicaType.Master, title: "Master", icon: "fa-solid fa-graduation-cap", done: false },
];

export default defineComponent({
  components: {
    RunaiStepper,
    DistributedHybridWorker,
    DistributedHybridMaster,
  },
  data() {
    return {
      appStore: useAppStore(),
      authStore: useAuthStore(),
      trainingStore: useTrainingStore(),
      clusterStore: useClusterStore(),
      dataSourceStore: useDataSourceStore(),
      workloadStore: useWorkloadStore(),

      submitting: false,
      steps: allSteps as Array<IStepsConfig>,
      currentStep: EDistributedReplicaType.Worker as string,

      distributedModel: workloadHybridUtil.getEmptyUISuperWorkloadModel() as IUISuperWorkloadCreationRequest,

      projectInfo: {} as UnifiedProjectInfo,
      selectedProjectNodeTypes: [] as string[],
      policyViewDetails: {} as IPolicyViewDetails,

      workerPolicy: undefined as UnifiedPolicyInfoPerReplicaType | undefined | null,
      masterPolicy: undefined as UnifiedPolicyInfoPerReplicaType | undefined | null,

      workerProjectNodePoolsResources: [] as StringOption[],
      masterProjectNodePoolsResources: [] as StringOption[],

      EDistributedReplicaType,
      EWorkloadFormType,

      secrets: [] as SecretListInfo[],
      allowedDatasourceOptions: undefined as Set<TDataSourceKinds> | undefined,
    };
  },
  setup() {
    const { environmentVariablesSourceValueOptions, loadSourceValueOptions, validateEnvironmentVariables } =
      useEnvironmentVariablesSection();

    return {
      environmentVariablesSourceValueOptions,
      loadSourceValueOptions,
      validateEnvironmentVariables,
    };
  },
  async created() {
    const { fromCopyId, templateId, projectId, clusterId, trainingName, distFramework, noMaster, workloadFormName } =
      this.$route.query;

    if (fromCopyId) this.trainingStore.setDistFlatFormCopyId(fromCopyId.toString());

    const formState = this.trainingStore.getDistributedWorkerFlatFormState();
    if (formState) {
      this.distributedModel = formState.workloadModel;
      this.projectInfo = formState.projectInfo;
      this.policyViewDetails = formState.policyViewDetails;
      this.selectedProjectNodeTypes = formState.selectedProjectNodeTypes;
      this.workerProjectNodePoolsResources = formState.projectNodePoolsResources;
      this.workerPolicy = formState.policy;

      const masterFormState = this.trainingStore.getDistributedMasterFlatFormState();
      if (masterFormState) {
        this.distributedModel = masterFormState.workloadModel;
        this.masterProjectNodePoolsResources = masterFormState.projectNodePoolsResources;
        this.masterPolicy = masterFormState.policy;
      }
    } else {
      await this.initDistributedModel(
        fromCopyId?.toString(),
        projectId?.toString(),
        clusterId?.toString(),
        trainingName?.toString(),
        templateId?.toString(),
        noMaster === "true",
        distFramework?.toString(),
      );
    }

    this.initRelevantFormSteps();
    if (workloadFormName) this.currentStep = workloadFormName as EDistributedReplicaType;

    await Promise.all([
      this.loadSecrets(),
      this.loadSourceValueOptions(true, this.distributedModel.projectId),
      this.loadAvailableDataSource(),
    ]);

    this.validateEnvironmentVariables(this.distributedModel.spec.environmentVariables || []);
    this.appStore.setPageLoading(false);
  },
  computed: {
    isPageReady(): boolean {
      return !this.appStore.isPageLoading;
    },
    projectData(): TProjectData {
      return {
        projectName: this.projectInfo.name || "",
        projectNodePoolsResources: this.workerProjectNodePoolsResources,
        selectedProjectNodeTypes: this.selectedProjectNodeTypes,
      };
    },
    sameAsWorkerDisabled(): boolean {
      return !!Object.keys(this.masterPolicy?.rules || {}).filter((k) => k !== "nodePools").length;
    },
    masterMetaData(): TDistributedMasterFormMetaData {
      return {
        projectId: this.distributedModel.projectId,
        clusterId: this.distributedModel.clusterId,
        framework: this.distributedModel.spec.distributedFramework || undefined,
        sameAsWorker: !!this.distributedModel.masterSpecSameAsWorker,
        sameAsWorkerDisabled: this.sameAsWorkerDisabled,
      };
    },
  },
  methods: {
    initRelevantFormSteps(): void {
      if (this.distributedModel.masterSpec) return;
      this.steps = allSteps.slice(0, 1);
    },
    onStepClicked(step: string): void {
      let isDone = true;
      this.steps = this.steps.map((currStep: IStepsConfig) => {
        if (step === currStep.name) {
          isDone = false;
        }

        return {
          ...currStep,
          done: isDone,
        };
      });

      this.currentStep = step;
    },
    handleNextStep(): void {
      // if master exists even as an empty object it means its not a "no master request" and we need to continue to the master step
      this.distributedModel.masterSpec ? this.onContinue() : this.onSubmit();
    },
    async onContinue(): Promise<void> {
      this.initMasterModel();
      this.steps[0].done = true;
      this.currentStep = EDistributedReplicaType.Master;
    },
    initMasterModel(): void {
      // if sameAsWorker is not possible we force it to be false and setting UI defaults for masterSpec
      if (this.sameAsWorkerDisabled) {
        this.setSameAsWorker(false);

        const masterUIModel = workloadHybridUtil.getMasterModelForUI(this.distributedModel.masterSpec || {}); // need to pass unified model

        // the following fields affect both worker and master:
        masterUIModel.terminationGracePeriodSeconds = this.distributedModel.spec.terminationGracePeriodSeconds;
        masterUIModel.autoDeletionTimeAfterCompletionSeconds =
          this.distributedModel.spec.autoDeletionTimeAfterCompletionSeconds;
        masterUIModel.backoffLimit = this.distributedModel.spec.backoffLimit;
        masterUIModel.cleanPodPolicy = this.distributedModel.spec.cleanPodPolicy;
        this.distributedModel.masterSpec = workloadHybridUtil.setDistributedMasterSpecUIDefaults(
          masterUIModel,
          this.authStore.isSSO,
          this.masterPolicy,
        );

        if (
          !this.trainingStore.distFlatFromCopyId &&
          !this.distributedModel.masterSpec?.nodePools?.length &&
          this.masterProjectNodePoolsResources?.[0]?.value
        )
          this.distributedModel.masterSpec.nodePools = [this.masterProjectNodePoolsResources[0].value];
        return;
      }
      // if its a copy and edit and master exists - do nothing we dont want to alter the original data
      if (this.trainingStore.distFlatFromCopyId && this.distributedModel.masterSpec) return;

      this.setSameAsWorker(true);
    },
    setSameAsWorker(sameAsWorker: boolean): void {
      this.distributedModel.masterSpecSameAsWorker = sameAsWorker;
      if (!sameAsWorker) return;

      const workerSpec = deepCopy(this.distributedModel.spec);
      delete workerSpec.numWorkers;
      delete workerSpec.podAffinity;
      delete workerSpec.distributedFramework;

      this.distributedModel.masterSpec = workerSpec;
    },
    updateMasterSpec(masterSpec: IUISuperWorkloadSpec): void {
      this.distributedModel.masterSpec = masterSpec;
      // the following fields affect both worker and master:
      this.distributedModel.spec.terminationGracePeriodSeconds = masterSpec.terminationGracePeriodSeconds;
      this.distributedModel.spec.autoDeletionTimeAfterCompletionSeconds =
        masterSpec.autoDeletionTimeAfterCompletionSeconds;
      this.distributedModel.spec.backoffLimit = masterSpec.backoffLimit;
      this.distributedModel.spec.cleanPodPolicy = masterSpec.cleanPodPolicy;
    },
    async onSubmit(): Promise<void> {
      try {
        this.submitting = true;

        this.distributedModel.spec = policyUtil.cleanupFlatItemizeBeforeSubmission(
          this.distributedModel.spec,
          this.workerPolicy,
        );

        if (this.distributedModel.masterSpec)
          this.distributedModel.masterSpec = policyUtil.cleanupFlatItemizeBeforeSubmission(
            this.distributedModel.masterSpec,
            this.masterPolicy,
          );

        const request = workloadHybridUtil.prepareDistributedFlatRequest(this.distributedModel);
        const createdDistributed = await trainingService.createDistributedFlat(request);

        if (createdDistributed) this.workloadStore.setWorkloadAdded(createdDistributed);

        this.$q.notify(alertUtil.getSuccess(`Training ${this.distributedModel.name} created`));
        this.redirectToPrevRoute(true);
      } catch (error: unknown) {
        const errorAlert = new ErrorAlert({
          generalMessage: ErrorAlert.failedCreateMessage("training"),
        });
        this.$q.notify(errorAlert.getNotification(error));
        console.error("Failed to create training:\n", error);
      } finally {
        this.submitting = false;
      }
    },
    redirectToPrevRoute(fromSubmit?: boolean): void {
      this.clearFormState();

      const query = fromSubmit ? { clusterId: this.distributedModel.clusterId } : undefined;
      this.$router.push({ name: WORKLOAD_ROUTE_NAMES.WORKLOAD_INDEX, query });
    },
    onBack(): void {
      this.steps[0].done = false;
      this.currentStep = EDistributedReplicaType.Worker;
    },
    async initDistributedModel(
      workloadId?: string,
      projectId?: string,
      clusterId?: string,
      trainingName?: string,
      templateId?: string,
      noMaster?: boolean,
      distFramework?: string,
    ): Promise<void> {
      try {
        const unifiedWorkload: UnifiedWorkload = await this.getUnifiedDistributed({
          workloadId,
          projectId,
          clusterId,
          assetId: templateId,
        });

        this.workerPolicy = unifiedWorkload.policyInfo?.worker;
        this.masterPolicy = unifiedWorkload.policyInfo?.master;

        this.policyViewDetails = getDistributedPolicyViewDetails({
          defaults: { worker: this.workerPolicy?.defaults, master: this.masterPolicy?.defaults },
          rules: { worker: this.workerPolicy?.rules, master: this.masterPolicy?.rules },
        });

        this.projectInfo = unifiedWorkload.projectInfo || {};
        this.workerProjectNodePoolsResources = unifiedWorkload.policyInfo?.worker?.rules?.nodePools?.options || [];
        this.masterProjectNodePoolsResources = unifiedWorkload.policyInfo?.master?.rules?.nodePools?.options || [];

        const uiModel = workloadHybridUtil.getHybridDistributedModel(unifiedWorkload, noMaster, !!workloadId);

        this.distributedModel = workloadHybridUtil.setDistributedUIDefaults(
          uiModel,
          this.authStore.isSSO,
          this.workerPolicy,
        );

        if (
          !workloadId &&
          !this.distributedModel.spec.nodePools?.length &&
          this.workerProjectNodePoolsResources?.[0]?.value
        )
          this.distributedModel.spec.nodePools = [this.workerProjectNodePoolsResources[0].value];

        // need to take for account the id coming from copy
        if (!this.distributedModel.spec.distributedFramework)
          this.distributedModel.spec.distributedFramework = distFramework as DistributedFramework;
        if (!this.distributedModel.spec.numWorkers) this.distributedModel.spec.numWorkers = 1;
        if (projectId) this.distributedModel.projectId = projectId;
        if (clusterId) this.distributedModel.clusterId = clusterId;
        if (trainingName) this.distributedModel.name = trainingName;

        this.setCleanPodPolicyDefaultValue();
      } catch (e) {
        this.$q.notify(alertUtil.getError("Failed to load training model"));
        console.error("Failed to get unified training:\n", e);
        this.appStore.setFallback(true);
      }
    },
    async getUnifiedDistributed(options?: UnifiedWorkloadRequest): Promise<UnifiedWorkload> {
      const request: UnifiedWorkloadRequest = {
        clusterId: options?.clusterId,
        projectId: options?.projectId,
        workloadType: EWorkloadType.Distributed,
        workloadId: options?.workloadId,
        assetId: options?.assetId,
        assetIds: options?.assetIds,
      };
      return await workloadService.getUnifiedWorkload(request);
    },
    async loadSecrets(): Promise<void> {
      try {
        this.secrets = await credentialService.listExistingSecrets(
          Scope.Project,
          this.distributedModel.projectId,
          undefined,
          "all",
          true,
        );
      } catch (error: unknown) {
        console.error("Failed to load secrets:\n", error);
        this.$q.notify(alertUtil.getError("Failed to load secrets"));
      }
    },
    saveWorkerFormState(selectedTemplates: THybridSelectedTemplates): void {
      const formState: THybridFormState = {
        projectInfo: this.projectInfo,
        projectNodePoolsResources: this.workerProjectNodePoolsResources,
        selectedTemplates: selectedTemplates,
        policy: this.workerPolicy,
        policyViewDetails: this.policyViewDetails,
        selectedProjectNodeTypes: this.selectedProjectNodeTypes,
        workloadModel: this.distributedModel,
      };
      this.trainingStore.setDistributedWorkerFlatFormState(deepCopy(formState));
    },
    saveMasterFormState(selectedTemplates: THybridSelectedTemplates): void {
      const formState: THybridFormState = {
        projectInfo: this.projectInfo,
        projectNodePoolsResources: this.masterProjectNodePoolsResources,
        selectedTemplates: selectedTemplates,
        policy: this.masterPolicy,
        policyViewDetails: this.policyViewDetails,
        selectedProjectNodeTypes: this.selectedProjectNodeTypes,
        workloadModel: this.distributedModel,
      };
      this.trainingStore.setDistributedMasterFlatFormState(deepCopy(formState));
    },
    clearFormState(): void {
      this.trainingStore.clearDistributedWorkerFlatFormState();
      this.trainingStore.clearDistributedMasterFlatFormState();
      this.trainingStore.setDistFlatFormCopyId(null);
    },
    async loadAvailableDataSource(): Promise<void> {
      this.allowedDatasourceOptions = await this.dataSourceStore.getAvailableDataSources();
      if (this.dataSourceStore.fallbackPolicyLoadingError) this.appStore.setFallback(true);
    },
    setCleanPodPolicyDefaultValue(): void {
      if (!this.distributedModel.spec.distributedFramework || !!this.distributedModel.spec.cleanPodPolicy) return;
      if (
        !this.clusterStore.isClusterVersionSufficient(
          this.distributedModel.clusterId,
          MIN_CLUSTER_VERSION_CLEAN_POD_POLICY,
        )
      )
        return;

      const defaultMapPerFramework = {
        [DistributedFramework.PyTorch]: DistributedCleanPodPolicy.None,
        [DistributedFramework.XgBoost]: DistributedCleanPodPolicy.All,
        [DistributedFramework.Tf]: DistributedCleanPodPolicy.Running,
        [DistributedFramework.Mpi]: DistributedCleanPodPolicy.None,
        [DistributedFramework.Jax]: DistributedCleanPodPolicy.None,
      };

      this.distributedModel.spec.cleanPodPolicy =
        defaultMapPerFramework[this.distributedModel.spec.distributedFramework];
    },
  },
});
</script>
